import styled, { css } from 'styled-components';

import rem from 'polished/lib/helpers/rem';
import spacing from '@decisiv/design-tokens/lib/spacing';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import { P, H3 } from '@decisiv/ui-components/lib/components/Typography';

interface ContainerProps {
  /**
   * When set, the title column shrinks to its content and the details column
   * takes the remaining width (used by the Depot header). Defaults to the
   * legacy even split so the Account/Management headers are unaffected.
   */
  $titleAutoWidth?: boolean;
  /**
   * Bottom padding in spacing units. Passed through a transient prop because
   * attrs win over a plain `paddingBottom` prop. Defaults to 0.5 (5px).
   */
  $paddingBottom?: number;
}

export const Container = styled(Flex).attrs<ContainerProps>(
  ({ $paddingBottom = 0.5 }) => ({
    paddingTop: 1.5,
    paddingX: 2,
    paddingBottom: $paddingBottom,
  }),
)<ContainerProps>`
  display: grid;
  grid-template-columns: ${({ $titleAutoWidth }) =>
    $titleAutoWidth ? 'auto 1fr' : '1fr 1fr'};
  ${({ $titleAutoWidth }) =>
    $titleAutoWidth &&
    css`
      align-items: center;
    `}
`;

export const Title = H3;

export const Left = styled(Flex)`
  align-items: center;
`;

export const Right = styled(Flex)`
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
`;

export const Actions = styled(Flex)`
  gap: ${rem(spacing.base * 0.5)};
  align-items: center;
`;

interface InformationProps {
  items: string[];
  /** Font size for the details line. Defaults to the base P size. */
  size?: 'small';
  /** Render the details in the muted secondary color. */
  muted?: boolean;
  /** Keep the details on a single line, truncating with an ellipsis. */
  singleLine?: boolean;
}

const InformationText = styled(P)<{ $singleLine?: boolean }>`
  ${({ $singleLine }) =>
    $singleLine &&
    css`
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    `}
`;

export const Information = ({
  items,
  size,
  muted,
  singleLine,
}: InformationProps) => {
  return (
    <InformationText
      size={size}
      color={muted ? 'alaskanHusky' : undefined}
      marginRight={2}
      $singleLine={singleLine}
    >
      {items.filter(Boolean).join(' • ')}
    </InformationText>
  );
};

export { DepotMenu } from './DepotMenu';
